<template>
	<div>
		<div ref="containerRef" class="datatype-input-container">
			<div :style="{ width: reserveUnknownButtonSpace ? 'calc(100% - 48px)' : '100%' }">
				<!-- date || time || timestamp || string || number -->
				<text-input
					v-if="useTextInput && datatype"
					:datatype="datatype"
					:value="value"
					:is-array="compProps.isArray"
					:autofocus="compProps.autofocus"
					:label="compProps.label"
					:instance-task="compProps.instanceTask"
					:attribute-id="attribute?.id"
					:question-color="templateTask?.color"
					@set="setValue"
				/>
				<!-- enumeration || boolean -->
				<enum-input
					v-else
					v-if="!!datatype"
					:datatype="datatype"
					:value="value"
					:attribute-id="attribute?.id"
					:is-array="compProps.isArray"
					:show-as-buttons="showAsButtons"
					:autofocus="compProps.autofocus"
					:label="compProps.label"
					:instance-task="compProps.instanceTask"
					:question-color="templateTask?.color"
					@set="setValue"
				/>
				<!-- reference: not gonna be editable by user -->
			</div>
			<!-- Unknown Button -->
			<div v-if="displayUnknownButton" class="unknown-button-wrapper" :style="unknownButtonStyle">
				<v-tooltip location="bottom" open-delay="750">
					<template v-slot:activator="{ props }">
						<v-btn
							size="small"
							v-bind="props"
							:class="[
								value == 'unknown' ? 'unknown-active' : 'unknown-opacity',
								{
									suggestionAnimationButton:
										questionsStore.tab === 'open' && suggestion === 'unknown',
								},
							]"
							:style="{ '--unknown-btn-color': questionColor, '--unknown-icon-color': unknownIconColor }"
							icon
							:variant="value == 'unknown' ? 'text' : 'plain'"
							tabindex="-1"
							@click="() => setValue(value == 'unknown' ? 'undefined' : 'unknown')"
						>
							<v-icon size="36">{{
								value == "unknown" ? "mdi-help-rhombus" : "mdi-help-rhombus-outline"
							}}</v-icon>
						</v-btn>
					</template>
					<span style="font-size: small">Nicht bekannt oder irrelevant</span>
				</v-tooltip>
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
import { DescriptorDatatype, InstanceTask } from "@lr-notitia/notitia-operator/schemas";
import { computed, CSSProperties, nextTick, onBeforeUnmount, onMounted, ref, watch } from "vue";
import { useGraphStore } from "../../../store/graph";
import { useQuestionsStore } from "../../../store/questions";
import { useGptSuggestionsStore } from "../../../store/gpt/suggestions";
import { useInstanceWorkspacesStore } from "../../../store/i-workspace";

import EnumInput from "./input/Enum.vue";
import TextInput from "./input/Text.vue";

const graphStore = useGraphStore();
const questionsStore = useQuestionsStore();
const gptSuggestionsStore = useGptSuggestionsStore();
const instanceWorkspacesStore = useInstanceWorkspacesStore();

const emits = defineEmits(["set"]);
const compProps = defineProps<{
	datatype: DescriptorDatatype | undefined;
	isArray: boolean;
	attributeId: string;
	label: string;
	autofocus: boolean;
	instanceTask: InstanceTask | undefined;
	showUnknownButton: string;
}>();

const isRenderedAsButtons = ref(false);

const attribute = computed(() => {
	return graphStore.instance.attribute.byId(compProps.attributeId);
});

const templateTask = computed(() => {
	return graphStore.template.task.byId(compProps.instanceTask?.template || "");
});

const displayUnknownButton = computed(() => {
	if (!displayUnknownButtonBecauseSettings.value) return false;
	if (isRenderedAsButtons.value) return true;
	return isValueUndefinedOrUnknown.value;
});

const reserveUnknownButtonSpace = computed(() => {
	if (isRenderedAsButtons.value) return displayUnknownButton.value;
	return displayUnknownButtonBecauseSettings.value;
});

// determine from settings whether to show the unknown button
const displayUnknownButtonBecauseSettings = computed(() => {
	// default
	let show = true;

	// global settings
	const templateWorkspace = graphStore.template.workspace.byId(
		instanceWorkspacesStore.openedWorkspace?.template || "",
	);
	if (templateWorkspace?.context?.show_unknownButton === false) show = false;
	else if (templateWorkspace?.context?.show_unknownButton === true) show = true;

	// individual settings
	if (compProps.showUnknownButton === "false") show = false;
	else if (compProps.showUnknownButton === "true") show = true;

	return show;
});

// if there is a value already set, we don't show the unknown button
const isValueUndefinedOrUnknown = computed(() => {
	return value.value === "undefined" || value.value === "unknown";
});

const suggestion = computed(() => {
	if (!compProps.instanceTask) return "";
	if (!attribute.value) return "";
	return gptSuggestionsStore.suggestionData[attribute.value?.id] || "";
});

const containerRef = ref<HTMLElement | null>(null);
const unknownButtonTop = ref<number>(24);
const unknownButtonTopOffset = 5;
const referenceControlHeight = 48;

const unknownButtonStyle = computed<CSSProperties>(() => {
	return {
		top: `${unknownButtonTop.value}px`,
	};
});

const questionColor = computed(() => {
	return templateTask.value?.color || "#4db6ac";
});

const unknownIconColor = computed(() => {
	const color = questionColor.value;
	if (!color || !color.startsWith("#")) return "inherit";
	const hex = color.replace("#", "");
	const r = parseInt(hex.substring(0, 2), 16) / 255;
	const g = parseInt(hex.substring(2, 4), 16) / 255;
	const b = parseInt(hex.substring(4, 6), 16) / 255;
	const luminance = 0.299 * r + 0.587 * g + 0.114 * b;
	return luminance < 0.5 ? "white" : "black";
});

let resizeObserver: ResizeObserver | undefined;

function syncRenderedMode() {
	const container = containerRef.value;
	isRenderedAsButtons.value = !!container?.querySelector(".enum-button-row");
}

function findAlignmentTarget(): HTMLElement | null {
	const container = containerRef.value;
	if (!container) return null;

	const enumButtonRow = container.querySelector(".enum-button-row") as HTMLElement | null;
	isRenderedAsButtons.value = !!enumButtonRow;
	if (enumButtonRow) return enumButtonRow;

	return (
		(container.querySelector(".question-input-field .v-input__control") as HTMLElement | null) ||
		(container.querySelector(".question-input-field .v-field") as HTMLElement | null) ||
		(container.querySelector(".question-input-field") as HTMLElement | null)
	);
}

function updateUnknownButtonPosition() {
	syncRenderedMode();
	if (!displayUnknownButton.value) return;
	const container = containerRef.value;
	const target = findAlignmentTarget();
	if (!container || !target) return;

	const containerRect = container.getBoundingClientRect();
	const targetRect = target.getBoundingClientRect();
	const dynamicCenterOffset = Math.max((targetRect.height - referenceControlHeight) / 2, 0);
	unknownButtonTop.value = targetRect.top - containerRect.top + unknownButtonTopOffset + dynamicCenterOffset;
}

function bindResizeObserver() {
	if (!containerRef.value || typeof ResizeObserver === "undefined") return;
	resizeObserver?.disconnect();

	resizeObserver = new ResizeObserver(() => {
		updateUnknownButtonPosition();
	});

	resizeObserver.observe(containerRef.value);
	const target = findAlignmentTarget();
	if (target) resizeObserver.observe(target);
}

const value = ref<any>("unknown");

function setValue(v: any) {
	value.value = v;
	emits("set", v);
	if (v !== "unknown") {
		questionsStore.hideFromOpenQuestions = questionsStore.hideFromOpenQuestions.filter(
			(q) => q !== compProps.instanceTask?.id,
		);
	}
}

const showAsButtons = computed(() => {
	if (compProps.isArray) return false;
	if (!compProps.datatype) return false;
	if (!compProps.datatype?.enumerationItems.length) return false;

	const subset: string[] | undefined = templateTask.value?.context?.datatypesubsets?.[attribute.value?.concept || ""];
	const count = subset ? subset.length : compProps.datatype.enumerationItems.length;
	return count <= 5;
});

const useTextInput = computed(() => {
	return (
		compProps.datatype?.id === "date" ||
		compProps.datatype?.id === "time" ||
		compProps.datatype?.id === "timestamp" ||
		compProps.datatype?.id === "string" ||
		compProps.datatype?.id === "number"
	);
});

watch(
	() => attribute.value?.value,
	() => (value.value = attribute.value?.value),
	{ immediate: true },
);

watch(
	[displayUnknownButton, showAsButtons, () => compProps.datatype?.id, suggestion, value],
	async () => {
		await nextTick();
		syncRenderedMode();
		bindResizeObserver();
		updateUnknownButtonPosition();
	},
	{ immediate: true },
);

onMounted(async () => {
	await nextTick();
	syncRenderedMode();
	bindResizeObserver();
	updateUnknownButtonPosition();
	window.addEventListener("resize", updateUnknownButtonPosition);
});

onBeforeUnmount(() => {
	window.removeEventListener("resize", updateUnknownButtonPosition);
	resizeObserver?.disconnect();
});
</script>

<style scoped lang="scss">
.datatype-input-container {
	position: relative;
}

.unknown-button-wrapper {
	position: absolute;
	right: 0;
}

.unknown-opacity {
	opacity: 50%;
	transition: background-color 0.1s ease, opacity 0.1s ease;

	&:hover {
		opacity: 100%;
		background-color: color-mix(in srgb, var(--unknown-btn-color) 50%, transparent) !important;
	}
}

.unknown-active {
	background-color: color-mix(in srgb, var(--unknown-btn-color) 80%, transparent) !important;
	color: var(--unknown-icon-color) !important;
}

.suggestionAnimationButton {
	animation: rainbowGlow 3s ease-in-out infinite;
}
@keyframes rainbowGlow {
	0% {
		box-shadow: 0 0 8px #f06292, 0 0 15px #f06292;
	}
	25% {
		box-shadow: 0 0 8px #ffb74d, 0 0 15px #ffb74d;
	}
	50% {
		box-shadow: 0 0 8px #81c784, 0 0 15px #81c784;
	}
	75% {
		box-shadow: 0 0 8px #9575cd, 0 0 15px #9575cd;
	}
	100% {
		box-shadow: 0 0 8px #e57373, 0 0 15px #e57373;
	}
}
</style>
